/**
 * FASE 15 · Centro de Conectividad UNIFICADO por empresa.
 *
 * Muestra TODAS las herramientas de la empresa activa (según módulos):
 *   - Redes sociales IG/FB/TT (real vía Composio)
 *   - WhatsApp Cloud
 *   - Email (Resend)
 *   - MercadoLibre (OAuth por empresa)
 *   - Messenger (Meta App global + page_access_token por empresa)
 *   - Facebook Marketplace (modo asistido, sin API oficial)
 *   - Meta Pages (reusa Composio)
 *
 * Cada tarjeta expone estado REAL / SIMULADO / Requiere-llave, cuentas
 * conectadas, acción principal, y mini-guía plegable "¿Cómo pasar a REAL?".
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SOCIAL_PLATFORMS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import SimulatedBanner from "@/components/SimulatedBanner";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Link } from "react-router-dom";
import {
  Instagram, Facebook, Music2, MessageCircle, Plug, PlugZap,
  Trash2, Loader2, CheckCircle2, Sparkles, ExternalLink, RefreshCw,
  Mail, Home, ShoppingBag, Newspaper, KeyRound, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = { Instagram, Facebook, Music2, MessageCircle };
const COMPOSIO_PLATFORMS = new Set(["instagram", "facebook", "tiktok"]);

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// Fase 28 · Bug A · función que decide el status de una tarjeta de red social
// respetando la nueva regla: "CONECTADA" requiere que exista al menos 1
// social_connection LOCAL vinculada (composio_account_id != null) en la
// empresa activa; sino, la tarjeta muestra "detectada" o "simulada".
function socialToolStatus({
  isWA, isComposio, isMaster,
  hasRealMapping, composioAvailable, composioReady, whatsappReady, localCount,
}) {
  if (isWA) {
    if (whatsappReady) return "real";
    // Sin llave del Master → mostrar aviso
    if (!isMaster && !whatsappReady) return "needs_key";
    return "simulated";
  }
  if (!isComposio) return "simulated";
  if (!composioReady) return isMaster ? "simulated" : "needs_key";
  // Composio configurado, ver estado local
  if (hasRealMapping) return "real";
  // Fase 28 · Detectada (Composio tiene cuentas) pero SIN vínculo local aún
  if (composioAvailable && localCount === 0) return "detected";
  if (composioAvailable && localCount > 0) return "detected";
  return "simulated";
}

// -----------------------------------------------------------------------------
// Sub-componentes
// -----------------------------------------------------------------------------
function StatusBadge({ status, testid }) {
  if (status === "real")
    return <Badge className="bg-brand-gold-soft text-brand-gold-dark border-brand-gold/50 font-semibold text-[10px]" data-testid={testid}>
      <Sparkles className="h-3 w-3 mr-1" /> REAL
    </Badge>;
  if (status === "connected")
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold text-[10px]" data-testid={testid}>
      <CheckCircle2 className="h-3 w-3 mr-1" /> CONECTADA
    </Badge>;
  if (status === "detected")
    return <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/40 font-semibold text-[10px]" data-testid={testid}>
      <CheckCircle2 className="h-3 w-3 mr-1" /> DETECTADA — falta vincular
    </Badge>;
  if (status === "needs_key")
    return <Badge variant="outline" className="text-stone-500 border-stone-300 text-[10px]" data-testid={testid}>
      Requiere llave del Master
    </Badge>;
  if (status === "assisted")
    return <Badge variant="outline" className="text-stone-600 border-stone-300 bg-stone-50 text-[10px]" data-testid={testid}>
      Modo asistido
    </Badge>;
  return <SimulatedBanner compact testid={testid} />;
}

function HowToActivate({ steps, testidPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 pt-3 border-t border-stone-200" data-testid={`${testidPrefix}-howto`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-xs uppercase tracking-widest font-semibold text-stone-500 hover:text-brand-emerald-dark flex items-center gap-1.5"
        data-testid={`${testidPrefix}-howto-toggle`}
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        ¿Cómo pasar a modo real?
      </button>
      {open && (
        <ol className="list-decimal ml-5 mt-2 text-sm text-stone-600 space-y-1 break-words [&_code]:break-all">
          {steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
    </div>
  );
}

function ToolCard({ icon: Icon, title, subtitle, accent = "#00A36C", status, testid, children, unlocks }) {
  return (
    <div className="card-elevated p-6 min-w-0 overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="h-11 w-11 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: accent }}>
            <Icon className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif-display text-xl text-brand-ink font-semibold truncate">{title}</div>
            {subtitle && <div className="text-xs text-stone-500 mt-0.5 break-words">{subtitle}</div>}
          </div>
        </div>
        <StatusBadge status={status} testid={`${testid}-status`} />
      </div>
      {unlocks && (
        <div className="text-[11px] text-stone-500 mb-3 italic break-words">
          <span className="font-semibold uppercase tracking-widest">Qué desbloquea:</span> {unlocks}
        </div>
      )}
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Componente principal
// -----------------------------------------------------------------------------
export default function Connectivity() {
  const { isMaster } = useAuth();
  const { active: activeCompany } = useCompany();
  const modules = activeCompany?.modules || {};

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogPlatform, setDialogPlatform] = useState(null);
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  const [mode, setMode] = useState({
    composio: false, whatsapp: false, resend: false, mercadolibre: false, meta_app: false,
    is_production: false, app_env: "preview",
  });
  const [composioAccounts, setComposioAccounts] = useState([]);
  const [composioError, setComposioError] = useState(null);
  const [composioLoading, setComposioLoading] = useState(false);
  const [mappingBusy, setMappingBusy] = useState({});
  const [oauthBusy, setOauthBusy] = useState({});
  const [refreshingComposio, setRefreshingComposio] = useState(false);
  const [idInputs, setIdInputs] = useState({});

  const [mlConnection, setMlConnection] = useState(null);
  const [mlBusy, setMlBusy] = useState(false);

  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const loadConnections = useCallback(async () => {
    try {
      const { data } = await api.get("/social/connections");
      if (mounted.current) setConnections(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar conexiones"); }
  }, []);

  const loadMode = useCallback(async () => {
    if (!isMaster) return;
    try {
      const { data } = await api.get("/integrations/mode/summary");
      if (mounted.current) setMode({
        composio: !!data?.composio, whatsapp: !!data?.whatsapp,
        resend: !!data?.resend, mercadolibre: !!data?.mercadolibre, meta_app: !!data?.meta_app,
        is_production: !!data?.is_production, app_env: data?.app_env || "preview",
      });
    } catch { /* ignore */ }
  }, [isMaster]);

  const loadComposio = useCallback(async () => {
    if (!isMaster) return;
    setComposioLoading(true);
    setComposioError(null);
    try {
      const r = await api.get("/integrations/composio/accounts");
      if (!mounted.current) return;
      if (r.data?.ok) {
        setComposioAccounts(r.data.accounts || []);
      } else {
        setComposioAccounts([]);
        setComposioError(r.data?.error || "No pudimos consultar Composio.");
      }
    } catch (e) {
      if (mounted.current) {
        setComposioAccounts([]);
        setComposioError(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Error de red al consultar Composio.");
      }
    } finally {
      if (mounted.current) setComposioLoading(false);
    }
  }, [isMaster]);

  const loadMercadoLibre = useCallback(async () => {
    if (!modules.mercadolibre) return;
    try {
      const { data } = await api.get("/mercadolibre/connection");
      if (mounted.current) setMlConnection(data);
    } catch { if (mounted.current) setMlConnection(null); }
  }, [modules.mercadolibre]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadConnections(), loadMode(), loadMercadoLibre()]);
      if (mounted.current) setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  useEffect(() => {
    if (mode.composio) loadComposio();
  }, [mode.composio, loadComposio]);

  // ---- Acciones ----
  const openDialog = (platform) => { setDialogPlatform(platform); setHandle(""); setDisplayName(""); };

  const submitConnect = async (e) => {
    e.preventDefault();
    if (!dialogPlatform) return;
    setSaving(true);
    try {
      await api.post("/social/connections", {
        platform: dialogPlatform.key, handle: handle.trim(),
        display_name: displayName.trim() || handle.trim(),
      });
      toast.success(`${dialogPlatform.label} conectado`);
      await loadConnections();
      if (mounted.current) setDialogPlatform(null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error"); }
    finally { if (mounted.current) setSaving(false); }
  };

  const disconnect = async (c) => {
    if (!window.confirm(`¿Desconectar la cuenta ${c.handle}?`)) return;
    try { await api.delete(`/social/connections/${c.id}`); toast.success("Cuenta desconectada"); await loadConnections(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  const openComposioOAuth = async (platform) => {
    setOauthBusy((b) => ({ ...b, [platform]: true }));
    try {
      // Fase 17 · el backend espera `platform` (no `toolkit`). Envío ambos por compat.
      const { data } = await api.post("/integrations/composio/connect-link", {
        platform, toolkit: platform,
      });
      if (data?.ok && data?.redirect_url) {
        window.open(data.redirect_url, "_blank", "noopener,noreferrer");
        toast.info("Autoriza en la ventana nueva y luego pulsa 'Ya autoricé — Refrescar'.");
      } else if (data && !data.ok) {
        toast.error(data.error || `Composio no pudo generar el enlace para ${platform}.`);
      } else {
        toast.error(`No se pudo generar el enlace de autorización para ${platform}.`);
      }
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Error al generar enlace.";
      toast.error(`Composio (${platform}): ${detail}`);
    }
    finally { if (mounted.current) setOauthBusy((b) => ({ ...b, [platform]: false })); }
  };

  const syncComposio = async () => {
    setRefreshingComposio(true);
    try {
      const { data } = await api.post("/integrations/composio/refresh-accounts");
      const found = data?.accounts?.length || 0;
      toast.success(`Se sincronizaron ${found} cuentas Composio activas.`);
      await Promise.all([loadComposio(), loadConnections()]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al sincronizar"); }
    finally { if (mounted.current) setRefreshingComposio(false); }
  };

  const setMapping = async (conn, composioAccountId) => {
    setMappingBusy((b) => ({ ...b, [conn.id]: true }));
    try {
      await api.patch(`/social/connections/${conn.id}/composio-mapping`, {
        composio_account_id: composioAccountId, composio_user_id: null,
      });
      toast.success(composioAccountId ? "Vinculada a Composio (modo REAL)" : "Vínculo eliminado");
      await loadConnections();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo mapear"); }
    finally { if (mounted.current) setMappingBusy((b) => ({ ...b, [conn.id]: false })); }
  };

  // Fase 28 · Bug A · Vincular una cuenta Composio detectada creando una
  // social_connection local en la empresa activa (idempotente en backend).
  const [linkBusy, setLinkBusy] = useState({});
  const [manualIds, setManualIds] = useState({}); // per platform: {handle, page_id, ig_user_id}
  const linkComposioAccount = async (composioAccountId, platform, extras = {}) => {
    setLinkBusy((b) => ({ ...b, [composioAccountId]: true }));
    try {
      await api.post("/integrations/composio/link-account", {
        composio_account_id: composioAccountId,
        platform,
        handle: extras.handle || null,
        display_name: extras.display_name || null,
        page_id: extras.page_id || null,
        ig_user_id: extras.ig_user_id || null,
      });
      toast.success("Cuenta vinculada — CONECTADA en modo REAL");
      await loadConnections();
      await loadComposio();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo vincular");
    } finally {
      if (mounted.current) setLinkBusy((b) => ({ ...b, [composioAccountId]: false }));
    }
  };

  const submitManualLink = async (platform) => {
    const m = manualIds[platform] || {};
    if (!m.composio_account_id || !m.handle) {
      toast.error("Ingresa el ID de la cuenta Composio y un handle");
      return;
    }
    await linkComposioAccount(m.composio_account_id, platform, m);
    setManualIds((prev) => ({ ...prev, [platform]: {} }));
  };

  // Fase 19 (fix) · page_id/ig_user_id no tenían campo de captura en la UI —
  // el backend ya los soporta en composio-mapping, solo faltaba el input.
  const saveConnectionId = async (conn) => {
    const idField = conn.platform === "facebook" ? "page_id" : "ig_user_id";
    const value = (idInputs[conn.id] ?? conn[idField] ?? "").trim();
    if (!value) { toast.error("Ingresa un ID antes de guardar."); return; }
    setMappingBusy((b) => ({ ...b, [conn.id]: true }));
    try {
      const body = {
        composio_account_id: conn.composio_account_id,
        composio_user_id: conn.composio_user_id || null,
        [idField]: value,
      };
      await api.patch(`/social/connections/${conn.id}/composio-mapping`, body);
      toast.success(idField === "page_id" ? "Page ID guardado" : "IG User ID guardado");
      setIdInputs((prev) => { const next = { ...prev }; delete next[conn.id]; return next; });
      await loadConnections();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar el ID"); }
    finally { if (mounted.current) setMappingBusy((b) => ({ ...b, [conn.id]: false })); }
  };

  const startMlOAuth = async () => {
    setMlBusy(true);
    try {
      const redirectUri = `${window.location.origin}/mercadolibre?callback=1`;
      const { data } = await api.post("/mercadolibre/connect/start", { redirect_uri: redirectUri });
      if (data?.authorization_url) { window.location.href = data.authorization_url; }
      else toast.error("No se pudo iniciar la conexión con MercadoLibre");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    finally { if (mounted.current) setMlBusy(false); }
  };

  const byPlatform = SOCIAL_PLATFORMS.map((p) => ({ ...p, accounts: connections.filter((c) => c.platform === p.key) }));

  // Fase 16 · Mapa toolkit→count de cuentas Composio conectadas (facebook/instagram/tiktok/whatsapp/gmail).
  const composioByToolkit = composioAccounts.reduce((acc, a) => {
    const tk = (a.toolkit || "").toLowerCase();
    if (!tk) return acc;
    acc[tk] = (acc[tk] || 0) + 1;
    return acc;
  }, {});
  const hasComposioForPlatform = (key) => (composioByToolkit[key] || 0) > 0;
  const hasWhatsAppSession = mode.whatsapp || hasComposioForPlatform("whatsapp");
  const hasGmailSession = hasComposioForPlatform("gmail");

  // ---- Guías estáticas por herramienta ----
  const stepsComposio = [
    <>El Master pega su <code>COMPOSIO_API_KEY</code> en <Link to="/integraciones" className="text-brand-emerald underline">/integraciones</Link>.</>,
    <>Aquí pulsa <strong>"Conectar {"{"}plataforma{"}"}"</strong> — se abre una ventana de Composio para autorizar tu cuenta real. <strong>No necesitas configurar nada en el dashboard de Composio</strong>: Composio gestiona el OAuth automáticamente.</>,
    <>Cuando autorices en la ventana, vuelve y pulsa <strong>"Ya autoricé — Refrescar"</strong>. Tu cuenta aparecerá con badge verde <strong>CONECTADA</strong>. Vincúlala a tu conexión local en el desplegable para pasar a <strong>REAL</strong>.</>,
  ];
  const stepsWhatsapp = [
    <>Crea un número aprobado en Meta Business y obtén el <code>ACCESS_TOKEN</code> permanente + <code>PHONE_NUMBER_ID</code>.</>,
    <>El Master los guarda en <Link to="/integraciones" className="text-brand-emerald underline">/integraciones</Link> → tarjeta WhatsApp Cloud → "Probar conexión".</>,
    <>Aprobar plantillas en Meta para poder iniciar conversaciones.</>,
  ];
  const stepsResend = [
    <>Cuenta en <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-brand-emerald underline">resend.com</a> + dominio verificado (SPF/DKIM).</>,
    <>El Master pega la <code>RESEND_API_KEY</code> en <Link to="/integraciones" className="text-brand-emerald underline">/integraciones</Link> → "Probar conexión".</>,
    <>Enviar campañas desde el módulo <Link to="/email-marketing" className="text-brand-emerald underline">Email Marketing</Link>.</>,
  ];
  const stepsMl = [
    <>El Master configura <code>client_id</code> + <code>client_secret</code> de MercadoLibre en <Link to="/integraciones" className="text-brand-emerald underline">/integraciones</Link>.</>,
    <>Aquí pulsa <strong>"Conectar cuenta de MercadoLibre"</strong> — abrirá la autorización oficial MLV para esta empresa.</>,
    <>Publicar avisos desde el módulo <Link to="/mercadolibre" className="text-brand-emerald underline">MercadoLibre</Link>.</>,
  ];
  const stepsMessenger = [
    <>El Master crea la App de Meta y guarda <code>app_id</code> + <code>app_secret</code> + <code>verify_token</code> en <Link to="/integraciones" className="text-brand-emerald underline">/integraciones</Link>.</>,
    <>Configura el webhook de Messenger apuntando a <code>/api/messenger/webhook</code> con el mismo verify_token.</>,
    <>En Conectividad → Facebook → añade el <code>page_access_token</code> de la Página en esta empresa.</>,
  ];

  return (
    <div className="space-y-6 animate-fade-in-up overflow-x-hidden" data-testid="connectivity-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <PlugZap className="h-8 w-8 text-brand-emerald" /> Centro de Conectividad
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-3xl">
          Hub unificado de la empresa <strong>{activeCompany?.name || "…"}</strong>. Aquí ves el estado real vs simulado
          de cada herramienta habilitada y activas las conexiones. Solo el rol <strong>Master</strong> puede modificarlas.
        </p>
      </div>

      {/* Fase 16 · Estado de la verificación Composio */}
      {isMaster && mode.composio && composioError && (
        <div className="rounded-lg bg-amber-50 border border-amber-300 p-4 flex items-start gap-3 flex-wrap" data-testid="composio-error-banner">
          <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <RefreshCw className="h-4 w-4 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-amber-900 text-sm">No pudimos consultar Composio</div>
            <div className="text-xs text-amber-800 mt-0.5 break-words">{composioError}</div>
          </div>
          <Button size="sm" variant="outline" onClick={loadComposio} disabled={composioLoading} data-testid="composio-error-retry"
            className="border-amber-400 text-amber-900 hover:bg-amber-100">
            {composioLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Reintentar
          </Button>
        </div>
      )}

      {loading ? (
        <div className="card-elevated p-16 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ---- Redes sociales (IG/FB/TT/WA) ---- */}
          {byPlatform.map((p) => {
            const Icon = ICON_MAP[p.icon] || Plug;
            const isComposioPlat = COMPOSIO_PLATFORMS.has(p.key);
            const isWA = p.key === "whatsapp";
            const hasRealMapping = p.accounts.some((a) => a.composio_account_id);
            const composioAvailable = hasComposioForPlatform(p.key);
            const toolStatus = socialToolStatus({
              isWA, isComposio: isComposioPlat, isMaster,
              hasRealMapping, composioAvailable,
              composioReady: mode.composio, whatsappReady: mode.whatsapp,
              localCount: p.accounts.length,
            });
            // Fase 28 · Bug A · cuentas Composio detectadas para esta plataforma
            // que aún NO están vinculadas a ninguna connection local.
            const linkedIds = new Set(p.accounts.map((a) => a.composio_account_id).filter(Boolean));
            const detectedUnlinked = composioAccounts.filter((a) =>
              (a.toolkit || "").toLowerCase() === p.key && !linkedIds.has(a.id)
            );
            return (
              <ToolCard
                key={p.key}
                icon={Icon} accent={p.accent}
                title={p.label}
                subtitle={`${p.accounts.length} cuenta${p.accounts.length === 1 ? "" : "s"}`}
                status={toolStatus}
                testid={`platform-card-${p.key}`}
                unlocks={isWA ? "Envío real de mensajes/plantillas WhatsApp." : "Publicación real programada y en el momento."}
              >
                {/* Fase 28 · Bug A · listar cuentas Composio DETECTADAS y sin vincular
                    con botón "Vincular esta cuenta" + entrada manual de IDs. */}
                {isMaster && isComposioPlat && mode.composio && detectedUnlinked.length > 0 && (
                  <div className="mb-3 rounded-lg bg-brand-emerald-soft/50 border border-brand-emerald/30 p-3" data-testid={`composio-detected-list-${p.key}`}>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-emerald-dark mb-2">
                      Cuentas Composio detectadas ({detectedUnlinked.length}) · empresa activa
                    </div>
                    <div className="space-y-1.5">
                      {detectedUnlinked.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 flex-wrap bg-white rounded px-2 py-1.5 border border-brand-emerald/20" data-testid={`detected-acc-${a.id}`}>
                          <span className="text-xs font-semibold text-brand-ink truncate flex-1 min-w-0">
                            {a.name || a.id.slice(0, 12)}
                          </span>
                          <code className="text-[10px] text-stone-500 font-mono truncate">{a.id.slice(0, 10)}…</code>
                          <Button
                            size="sm"
                            onClick={() => linkComposioAccount(a.id, p.key, {
                              handle: a.name || a.id.slice(0, 16),
                              display_name: a.name || null,
                            })}
                            disabled={!!linkBusy[a.id]}
                            data-testid={`link-detected-${a.id}`}
                            className="h-7 bg-brand-emerald text-white hover:bg-brand-emerald-dark text-xs"
                          >
                            {linkBusy[a.id] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plug className="h-3 w-3 mr-1" />}
                            Vincular
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fase 28 · Bug A · entrada MANUAL para pegar un connection_id
                    (aparece siempre para IG/FB cuando Composio está activo). */}
                {isMaster && (p.key === "instagram" || p.key === "facebook") && mode.composio && (
                  <div className="mb-3 rounded-lg border border-dashed border-stone-300 bg-white p-3" data-testid={`manual-link-${p.key}`}>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
                      Entrada manual · vincular por ID
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Input
                        placeholder="ID de cuenta Composio (ca_...)"
                        className="h-8 text-xs"
                        value={(manualIds[p.key]?.composio_account_id) || ""}
                        onChange={(e) => setManualIds((prev) => ({
                          ...prev,
                          [p.key]: { ...(prev[p.key] || {}), composio_account_id: e.target.value.trim() },
                        }))}
                        data-testid={`manual-composio-id-${p.key}`}
                      />
                      <Input
                        placeholder={p.key === "facebook" ? "Nombre de la página" : "@handle IG"}
                        className="h-8 text-xs"
                        value={(manualIds[p.key]?.handle) || ""}
                        onChange={(e) => setManualIds((prev) => ({
                          ...prev,
                          [p.key]: { ...(prev[p.key] || {}), handle: e.target.value },
                        }))}
                        data-testid={`manual-handle-${p.key}`}
                      />
                      <Input
                        placeholder={p.key === "facebook" ? "Page ID (opcional)" : "IG User ID (opcional)"}
                        className="h-8 text-xs"
                        value={(manualIds[p.key]?.[p.key === "facebook" ? "page_id" : "ig_user_id"]) || ""}
                        onChange={(e) => setManualIds((prev) => ({
                          ...prev,
                          [p.key]: {
                            ...(prev[p.key] || {}),
                            [p.key === "facebook" ? "page_id" : "ig_user_id"]: e.target.value.trim(),
                          },
                        }))}
                        data-testid={`manual-id-${p.key}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => submitManualLink(p.key)}
                        className="h-8 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark text-xs"
                        data-testid={`manual-link-submit-${p.key}`}
                      >
                        Vincular manualmente
                      </Button>
                    </div>
                    <p className="text-[10px] text-stone-500 mt-1.5">
                      Úsalo si Composio no detecta la cuenta automáticamente o tienes el ID a mano.
                    </p>
                  </div>
                )}

                {/* Fase 16 · Aviso de cuentas Composio detectadas para esta plataforma */}
                {isMaster && isComposioPlat && mode.composio && composioAvailable && (
                  <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid={`composio-detected-${p.key}`}>
                    <CheckCircle2 className="h-4 w-4 text-brand-emerald shrink-0" />
                    <span><strong>{composioByToolkit[p.key]}</strong> cuenta{composioByToolkit[p.key] === 1 ? "" : "s"} detectada{composioByToolkit[p.key] === 1 ? "" : "s"} en Composio. {p.accounts.length === 0 ? "Agrega la cuenta local abajo y vincúlala." : "Vincúlala en el desplegable de cada cuenta."}</span>
                  </div>
                )}
                {isWA && isMaster && (
                  <div className="mb-3 rounded-lg bg-brand-gold-soft/40 border border-brand-gold/30 px-3 py-2 text-xs text-stone-700" data-testid="wa-qr-notice">
                    <strong className="text-brand-ink">Conexión Maestra WhatsApp:</strong> use la API oficial (Cloud API con
                    <em> access_token + phone_number_id</em>) desde <Link to="/integraciones" className="underline font-semibold text-brand-emerald-dark">Integraciones → WhatsApp</Link>.
                    <span className="block mt-1 text-stone-500">
                      La conexión por QR no está disponible en la API oficial de WhatsApp.
                    </span>
                  </div>
                )}
                {isWA && !mode.whatsapp && hasComposioForPlatform("whatsapp") && (
                  <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid={`composio-detected-whatsapp`}>
                    <CheckCircle2 className="h-4 w-4 text-brand-emerald shrink-0" />
                    <span>Sesión WhatsApp detectada en Composio. Puedes usar el <Link to="/whatsapp" className="underline font-semibold">panel de WhatsApp</Link>.</span>
                  </div>
                )}
                <div className="space-y-2">
                  {p.accounts.length === 0 && (
                    <div className="rounded-lg border border-dashed border-stone-300 py-6 text-center text-sm text-stone-500">
                      Sin cuentas conectadas.
                    </div>
                  )}
                  {p.accounts.map((c) => {
                    const platformAccounts = composioAccounts.filter((a) => (a.toolkit || "").toLowerCase() === c.platform);
                    const isReal = c.mode === "real" && !!c.composio_account_id;
                    // Fase 17 · una conexión simulada cuando el provider ya está REAL es "heredada".
                    const isLegacySim = isComposioPlat && mode.composio && !isReal;
                    return (
                      <div key={c.id} className="rounded-lg border border-border bg-white px-4 py-3 min-w-0" data-testid={`connection-row-${c.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex items-center gap-2 flex-1">
                            <span className="font-semibold text-brand-ink truncate min-w-0">{c.handle}</span>
                            {isReal
                              ? <Badge className="bg-brand-gold-soft text-brand-gold-dark border-brand-gold/50 font-semibold text-[10px]" data-testid={`connection-mode-real-${c.id}`}><Sparkles className="h-3 w-3 mr-1" /> REAL</Badge>
                              : isLegacySim
                                ? <Badge variant="outline" className="text-stone-500 border-stone-300 bg-stone-50 text-[10px]" data-testid={`connection-legacy-${c.id}`}>heredada — simulada</Badge>
                                : <SimulatedBanner compact testid={`connection-badge-${c.id}`} />
                            }
                          </div>
                          {isMaster && (
                            <Button variant="ghost" size="icon" onClick={() => disconnect(c)} data-testid={`disconnect-${c.id}`} className="text-destructive hover:bg-destructive/10">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="text-xs text-stone-500 mt-1 flex items-center gap-1.5 flex-wrap">
                          <CheckCircle2 className="h-3.5 w-3.5 text-brand-emerald" />
                          <span>{formatDate(c.connected_at)}</span><span>·</span><span>por {c.connected_by_name}</span>
                        </div>
                        {isMaster && isComposioPlat && mode.composio && (
                          <div className="mt-3 pt-3 border-t border-stone-200 flex items-center gap-2 flex-wrap" data-testid={`composio-mapping-row-${c.id}`}>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Cuenta Composio</Label>
                            <div className="flex-1 min-w-[180px]">
                              <Select
                                key={`sel-${c.id}-${c.composio_account_id || 'none'}`}
                                value={c.composio_account_id || "__none__"}
                                onValueChange={(v) => setMapping(c, v === "__none__" ? null : v)}
                                disabled={!!mappingBusy[c.id]}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`composio-mapping-select-${c.id}`}>
                                  <SelectValue placeholder={platformAccounts.length === 0 ? `Sin cuentas Composio ${p.label}` : "Sin vincular"} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Sin vincular (simulado)</SelectItem>
                                  {/* Fase 16 · Si el value actual ya no está en accounts (borrado en Composio),
                                      lo incluimos como opción huérfana para no romper Radix Select. */}
                                  {c.composio_account_id && !platformAccounts.some((a) => a.id === c.composio_account_id) && (
                                    <SelectItem value={c.composio_account_id}>
                                      (No encontrada) {c.composio_account_id.slice(0, 10)}…
                                    </SelectItem>
                                  )}
                                  {platformAccounts.map((a) => (
                                    <SelectItem key={a.id} value={a.id} data-testid={`composio-mapping-option-${a.id}`}>
                                      {a.name || 'Sin nombre'} · {a.id.slice(0, 10)}…
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                        {isMaster && c.composio_account_id && (c.platform === "facebook" || c.platform === "instagram") && (
                          <div className="mt-2 pt-2 border-t border-stone-100 flex items-end gap-2 flex-wrap" data-testid={`composio-ids-row-${c.id}`}>
                            <div className="flex-1 min-w-[180px]">
                              <Label className="text-[10px] uppercase tracking-widest text-stone-500">
                                {c.platform === "facebook" ? "Page ID (Facebook)" : "IG User ID (Instagram Business)"}
                              </Label>
                              <Input
                                className="h-8 text-xs"
                                placeholder={c.platform === "facebook" ? "ID numérico de tu Página" : "ID numérico de tu cuenta IG Business"}
                                value={idInputs[c.id] ?? (c.platform === "facebook" ? (c.page_id || "") : (c.ig_user_id || ""))}
                                onChange={(e) => setIdInputs((prev) => ({ ...prev, [c.id]: e.target.value }))}
                                disabled={!!mappingBusy[c.id]}
                                data-testid={`composio-id-input-${c.id}`}
                              />
                            </div>
                            <Button size="sm" variant="outline" onClick={() => saveConnectionId(c)}
                              disabled={!!mappingBusy[c.id]} data-testid={`composio-id-save-${c.id}`}>
                              {mappingBusy[c.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {isMaster && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {/* Fase 17 · Ocultar "Agregar cuenta" para IG/FB/TT si Composio ya está
                        configurado (no queremos crear más simuladas cuando hay llave real).
                        En preview sin llave sí lo mostramos para poder seguir probando. */}
                    {(!isComposioPlat || !mode.composio) && (
                      <Button size="sm" onClick={() => openDialog(p)} data-testid={`connect-btn-${p.key}`} className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                        <Plug className="h-4 w-4 mr-1.5" /> {isWA ? "Agregar número" : "Agregar cuenta"}
                      </Button>
                    )}
                    {isComposioPlat && mode.composio && (
                      <Button size="sm" variant="outline" onClick={() => openComposioOAuth(p.key)}
                        disabled={!!oauthBusy[p.key]} data-testid={`oauth-btn-${p.key}`}>
                        {oauthBusy[p.key] ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
                        Conectar {p.label}
                      </Button>
                    )}
                    {isComposioPlat && mode.composio && (
                      <Button size="sm" variant="ghost" onClick={syncComposio}
                        disabled={refreshingComposio} data-testid={`refresh-btn-${p.key}`}
                        className="text-brand-emerald hover:bg-emerald-50">
                        {refreshingComposio ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                        Ya autoricé — Refrescar
                      </Button>
                    )}
                    {isComposioPlat && !mode.composio && (
                      <Link to="/integraciones" className="text-xs text-brand-emerald underline flex items-center gap-1">
                        <KeyRound className="h-3.5 w-3.5" /> Master: configura Composio
                      </Link>
                    )}
                  </div>
                )}
                {/* Fase 16 · CTA directo al panel del módulo WhatsApp (equivalente a los shortcuts de las demás tarjetas). */}
                {isWA && (
                  <div className="mt-3">
                    <Link to="/whatsapp" data-testid="wa-open-panel">
                      <Button size="sm" variant="outline" className="w-full sm:w-auto">
                        <MessageCircle className="h-4 w-4 mr-1.5" /> Abrir panel de WhatsApp
                      </Button>
                    </Link>
                  </div>
                )}

                <HowToActivate
                  testidPrefix={`platform-${p.key}`}
                  steps={isWA ? stepsWhatsapp : stepsComposio}
                />
              </ToolCard>
            );
          })}

          {isMaster && mode.composio && (
            <div className="md:col-span-2 flex justify-end -mt-2">
              <Button variant="outline" onClick={syncComposio} disabled={refreshingComposio} data-testid="sync-composio-btn">
                {refreshingComposio ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                Sincronizar cuentas Composio
              </Button>
            </div>
          )}

          {/* ---- Email marketing (Resend / Gmail vía Composio) ---- */}
          {modules.email_marketing && (
            <ToolCard icon={Mail} accent="#7C5CFA" title="Email Marketing"
              subtitle={hasGmailSession ? "Gmail conectado vía Composio · Resend para envíos masivos" : "Envíos y tracking con Resend"}
              status={mode.resend ? "real" : (hasGmailSession ? "connected" : (isMaster ? "simulated" : "needs_key"))}
              testid="tool-card-email"
              unlocks="Envío real de campañas, tracking de aperturas y clics con dominio verificado.">
              {hasGmailSession && (
                <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="gmail-composio-detected">
                  <CheckCircle2 className="h-4 w-4 text-brand-emerald shrink-0" />
                  <span>Cuenta Gmail conectada en Composio. Los envíos masivos siguen usando Resend; el envío individual por Gmail vía Composio queda como próximo paso.</span>
                </div>
              )}
              <p className="text-sm text-stone-600">
                Configura remitentes desde <Link to="/email-marketing" className="text-brand-emerald underline">Email Marketing</Link>.
              </p>
              <HowToActivate testidPrefix="tool-email" steps={stepsResend} />
            </ToolCard>
          )}

          {/* ---- MercadoLibre ---- */}
          {modules.mercadolibre && (
            <ToolCard icon={Home} accent="#FFE600" title="MercadoLibre"
              subtitle={mlConnection?.connected ? `Conectada como ${mlConnection.user_nickname || "usuario ML"}` : "Sin conectar"}
              status={!mode.mercadolibre ? "needs_key" : (mlConnection?.connected ? "real" : "simulated")}
              testid="tool-card-mercadolibre"
              unlocks="Publicación real de avisos inmobiliarios en MercadoLibre Venezuela.">
              {isMaster && mode.mercadolibre && !mlConnection?.connected && (
                <Button size="sm" onClick={startMlOAuth} disabled={mlBusy} data-testid="ml-oauth-btn"
                  className="bg-brand-emerald text-white hover:bg-brand-emerald-dark">
                  {mlBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
                  Conectar cuenta de MercadoLibre
                </Button>
              )}
              {mlConnection?.connected && (
                <div className="text-xs text-stone-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <CheckCircle2 className="h-3.5 w-3.5 inline mr-1 text-brand-emerald" />
                  Token guardado. Puedes publicar desde <Link to="/mercadolibre" className="text-brand-emerald underline">MercadoLibre</Link>.
                </div>
              )}
              {!mode.mercadolibre && isMaster && (
                <div className="text-xs text-stone-500">
                  <Link to="/integraciones" className="text-brand-emerald underline">Master: configura la app de MercadoLibre</Link>
                </div>
              )}
              <HowToActivate testidPrefix="tool-mercadolibre" steps={stepsMl} />
            </ToolCard>
          )}

          {/* ---- Messenger ---- */}
          {modules.messenger && (
            <ToolCard icon={MessageCircle} accent="#0084FF" title="Messenger"
              subtitle="Bandeja de mensajes por página de Facebook"
              status={mode.meta_app ? "real" : (isMaster ? "simulated" : "needs_key")}
              testid="tool-card-messenger"
              unlocks="Recepción y respuesta real de mensajes en la Página de Facebook.">
              <p className="text-sm text-stone-600">
                Para modo REAL cada empresa debe agregar su <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded break-all">page_access_token</code> en la
                conexión de Facebook (arriba, botón "Agregar cuenta").
                Bandeja en <Link to="/messenger" className="text-brand-emerald underline">Messenger</Link>.
              </p>
              <HowToActivate testidPrefix="tool-messenger" steps={stepsMessenger} />
            </ToolCard>
          )}

          {/* ---- Marketplace (informativo) ---- */}
          {modules.marketplace && (
            <ToolCard icon={ShoppingBag} accent="#1877F2" title="Facebook Marketplace"
              subtitle="Modo asistido (Facebook no ofrece API pública)"
              status="assisted"
              testid="tool-card-marketplace"
              unlocks="Cola de anuncios con jitter humano; publicación asistida por operador.">
              <p className="text-sm text-stone-600">
                Facebook Marketplace no expone API oficial. El módulo <Link to="/marketplace" className="text-brand-emerald underline">Marketplace</Link> mantiene la cola
                con jitter de 7–25 min. Un operador confirma la publicación en Facebook.
              </p>
            </ToolCard>
          )}

          {/* ---- Meta Pages ---- */}
          {modules.meta_pages && (
            <ToolCard icon={Newspaper} accent="#1877F2" title="Meta Pages"
              subtitle="Publicación en la Página de Facebook"
              status={mode.composio ? "real" : (isMaster ? "simulated" : "needs_key")}
              testid="tool-card-meta-pages"
              unlocks="Publicación real de posts en la Página de Facebook (reusa Composio).">
              <p className="text-sm text-stone-600">
                Usa la misma conexión de Facebook de arriba. Verifica que la cuenta Composio de Facebook esté vinculada.
                Publica desde <Link to="/meta-pages" className="text-brand-emerald underline">Meta Pages</Link>.
              </p>
              <HowToActivate testidPrefix="tool-meta-pages" steps={stepsComposio} />
            </ToolCard>
          )}
        </div>
      )}

      {/* ---- Dialog conectar cuenta (simulado o handle inicial) ---- */}
      <Dialog open={!!dialogPlatform} onOpenChange={(v) => !v && setDialogPlatform(null)}>
        <DialogContent data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              Agregar cuenta {dialogPlatform?.label}
            </DialogTitle>
            <DialogDescription>
              Registra la cuenta en el sistema. Para envío/publicación REAL debes luego vincularla a Composio (redes) o pegar el token (WhatsApp/Messenger).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitConnect} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Handle / Cuenta</Label>
              <Input required value={handle} onChange={(e) => setHandle(e.target.value)}
                placeholder={dialogPlatform?.placeholderHandle} data-testid="connect-handle-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest font-semibold text-stone-600">Nombre a mostrar (opcional)</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Nombre visible para el equipo" data-testid="connect-display-name-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogPlatform(null)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="connect-submit"
                className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold">
                {saving ? "Guardando…" : "Guardar cuenta"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
